import { useCallback, useRef, useState } from "react";
import { Gem, Map, Lock, X } from "lucide-react";
import MarketScanner from "./MarketScanner";
import HowToPlay from "./HowToPlay";
import Confetti from "./Confetti";
import { useAutoRun, AutoRunBar, BurstCooldownChip } from "./AutoRun";
import "./arcade-anim.css";

/* 💎 GEM RUSH — casino-style Candy-Crush board on REAL Deriv Digit Over contracts.
   SLIDE a gem into a neighbour (or tap it) → gems swap → one contract fires.
   WIN → the matched colour group explodes and new gems slide in. LOSE → the
   swap snaps back and the gem cracks. GOLD gems and the bonus meter grant
   EXTRA LEVEL PROGRESS only — they never change odds, payouts or stakes. */
const TIERS = [
  { key: "sweet", name: "SWEET", bar: 0, odds: "90%", pays: "~×1.08", color: "#089981", desc: "wins on digit 1–9", minBal: 60, unlock: 1 },
  { key: "juicy", name: "JUICY", bar: 1, odds: "80%", pays: "~×1.18", color: "#F7B731", desc: "wins on digit 2–9", minBal: 40, unlock: 3 },
  { key: "wild", name: "WILD", bar: 2, odds: "70%", pays: "~×1.35", color: "#F23645", desc: "wins on digit 3–9", minBal: 25, unlock: 6 },
];
const THEMES = [
  { name: "CANDY FIELDS", tint: "rgba(100,40,160,0.34)", accent: "#b86bff" },
  { name: "JUNGLE JAM", tint: "rgba(10,90,45,0.38)", accent: "#3ddc84" },
  { name: "OCEAN DEEP", tint: "rgba(12,60,130,0.40)", accent: "#4dabff" },
  { name: "VOLCANO VAULT", tint: "rgba(130,35,10,0.38)", accent: "#ff8c42" },
];
const winsNeeded = (level) => Math.min(2 + level, 10); // L1=3, L2=4 … max 10
const themeFor = (level) => THEMES[Math.floor((level - 1) / 3) % THEMES.length];
const COLS = 7;
const ROWS = 8;
const N = COLS * ROWS;
const GEMS = [
  { c: "#ff4d6d", g: "radial-gradient(circle at 32% 28%, #ff9eb1, #ff4d6d 55%, #b3123a)", r: "14px" },
  { c: "#4dabff", g: "radial-gradient(circle at 32% 28%, #a8d6ff, #4dabff 55%, #1456b8)", r: "50%" },
  { c: "#3ddc84", g: "radial-gradient(circle at 32% 28%, #a5f3c9, #3ddc84 55%, #0e9d53)", r: "8px" },
  { c: "#ffd24d", g: "radial-gradient(circle at 32% 28%, #ffeaa8, #ffd24d 55%, #c79a0a)", r: "50% 10px" },
  { c: "#b86bff", g: "radial-gradient(circle at 32% 28%, #ddb8ff, #b86bff 55%, #7a24c9)", r: "10px 50%" },
  { c: "#ff8c42", g: "radial-gradient(circle at 32% 28%, #ffc79e, #ff8c42 55%, #c2591a)", r: "18px 6px" },
];
const GOLD = { g: "radial-gradient(circle at 32% 28%, #fff3b0, #ffd700 45%, #9e7400)", r: "50%" };
const rnd = () => Math.floor(Math.random() * GEMS.length);
/* 🍬 Candy-Crush rule: the board NEVER spawns with a ready-made 3-match —
   you have to create the match yourself. */
const makes3 = (b, i, t) => {
  const r = Math.floor(i / COLS), c = i % COLS;
  const get = (rr, cc) => (rr < 0 || cc < 0 || rr >= ROWS || cc >= COLS) ? undefined : b[rr * COLS + cc]?.t;
  if (c >= 2 && get(r, c - 1) === t && get(r, c - 2) === t) return true;
  if (c <= COLS - 3 && get(r, c + 1) === t && get(r, c + 2) === t) return true;
  if (c >= 1 && c <= COLS - 2 && get(r, c - 1) === t && get(r, c + 1) === t) return true;
  if (r >= 2 && get(r - 1, c) === t && get(r - 2, c) === t) return true;
  if (r <= ROWS - 3 && get(r + 1, c) === t && get(r + 2, c) === t) return true;
  if (r >= 1 && r <= ROWS - 2 && get(r - 1, c) === t && get(r + 1, c) === t) return true;
  return false;
};
const safeType = (b, i) => { for (let k = 0; k < 14; k++) { const t = rnd(); if (!makes3(b, i, t)) return t; } return rnd(); };
const mkGem = (tag = "") => ({ id: `g${Date.now()}_${tag}_${Math.random().toString(36).slice(2, 7)}`, t: rnd(), gold: Math.random() < 0.07, fx: "" });
const freshBoard = () => {
  const cells = [];
  for (let i = 0; i < N; i++) {
    const g = mkGem(i);
    g.t = safeType(cells, i);
    if (g.gold && (cells[i - 1]?.gold || cells[i - COLS]?.gold)) g.gold = false; // no twin golds side by side
    cells.push(g);
  }
  return cells;
};
const LVL_KEY = "vts_gemrush_level";
const METER_MAX = 4;

const GemRush = ({ runContract, busy, balance = 0 }) => {
  const [lvl, setLvl] = useState(() => { try { const v = JSON.parse(localStorage.getItem(LVL_KEY) || '{"level":1,"wins":0}'); return v.level >= 1 ? v : { level: 1, wins: 0 }; } catch { return { level: 1, wins: 0 }; } });
  const [tier, setTier] = useState(TIERS[0]);
  const [board, setBoard] = useState(freshBoard);
  const [phase, setPhase] = useState("idle"); // idle | match | win | lose
  const [sel, setSel] = useState(null);
  const [last, setLast] = useState(null);
  const [combo, setCombo] = useState(0);
  const [meter, setMeter] = useState(0); // 🎰 bonus meter — fills on wins
  const [bonusMsg, setBonusMsg] = useState(null);
  const [levelUp, setLevelUp] = useState(null); // { cleared, next, unlockName }
  const [mapOpen, setMapOpen] = useState(false);
  const [swapFx, setSwapFx] = useState(null); // { i, j } sliding pair
  const timer = useRef(null);
  const selRef = useRef(null);
  const dragRef = useRef(null);
  const swapRef = useRef(null); // last committed swap, for revert on loss
  const lineRef = useRef(null); // the 3-line match formed on a win
  const boardRef = useRef(board);
  boardRef.current = board;
  const symRef = useRef("1HZ100V");
  const onScan = useCallback((row) => { symRef.current = row.sym; }, []);
  const auto = useAutoRun("gems");

  const theme = themeFor(lvl.level);
  const need = winsNeeded(lvl.level);
  const saveLvl = (v) => { setLvl(v); try { localStorage.setItem(LVL_KEY, JSON.stringify(v)); } catch { /* noop */ } };

  // 🏆 add n wins of level progress (gold gems / full meter give extras)
  const addProgress = (n) => {
    let { level, wins } = lvl;
    wins += n;
    let cleared = null, unlockName = null;
    while (wins >= winsNeeded(level)) {
      wins -= winsNeeded(level);
      cleared = level;
      level += 1;
      const u = TIERS.find((t) => t.unlock === level);
      if (u) unlockName = u.name;
    }
    saveLvl({ level, wins });
    if (cleared != null) { setLevelUp({ cleared, next: level, unlockName }); setTimeout(() => setLevelUp(null), 3200); }
  };

  const cascade = (idx) => {
    // 🍬 the win forms a visible 3-LINE match through the played gem, then explodes
    setBoard((b) => {
      const color = b[idx]?.t ?? 0;
      const r = Math.floor(idx / COLS), c = idx % COLS;
      const lines = [];
      if (c <= COLS - 3) lines.push([idx, idx + 1, idx + 2]);
      if (c >= 2) lines.push([idx, idx - 1, idx - 2]);
      if (r <= ROWS - 3) lines.push([idx, idx + COLS, idx + 2 * COLS]);
      if (r >= 2) lines.push([idx, idx - COLS, idx - 2 * COLS]);
      const line = lines[Math.floor(Math.random() * lines.length)] || [idx];
      lineRef.current = line;
      return b.map((g, i) => (line.includes(i) ? { ...g, t: color, fx: "merge" } : g));
    });
    setTimeout(() => {
      setBoard((b) => {
        const color = b[idx]?.t ?? 0;
        const line = lineRef.current || [idx];
        const extra = b.map((g, i) => (g.t === color && !line.includes(i) ? i : -1)).filter((i) => i >= 0).sort(() => Math.random() - 0.5).slice(0, 2);
        const pop = new Set([...line, ...extra]);
        return b.map((g, i) => (pop.has(i) ? { ...g, fx: "pop" } : g));
      });
      setTimeout(() => {
        setBoard((b) => {
          const nb = [...b];
          nb.forEach((g, i) => {
            if (g.fx === "pop") {
              const ng = mkGem("c"); ng.t = safeType(nb, i); ng.fx = "fall";
              if (ng.gold && (nb[i - 1]?.gold || nb[i + 1]?.gold || nb[i - COLS]?.gold || nb[i + COLS]?.gold)) ng.gold = false;
              nb[i] = ng;
            }
          });
          return nb;
        });
        setTimeout(() => setBoard((b) => b.map((g) => (g.fx === "fall" ? { ...g, fx: "" } : g))), 520);
      }, 430);
    }, 220);
  };

  const play = async () => {
    if (busy) return null;
    clearTimeout(timer.current);
    let idx = selRef.current;
    if (idx == null) idx = Math.floor(Math.random() * N); // auto-run picks a random gem
    const isGold = !!boardRef.current[idx]?.gold;
    setSel(idx);
    setPhase("match");
    setBonusMsg(null);
    setBoard((b) => b.map((g, i) => (i === idx ? { ...g, fx: "pulse" } : g)));
    const res = await runContract({
      contractType: "DIGITOVER", barrier: tier.bar, side: `OVER ${tier.bar}`,
      label: "Gem Rush", symbol: symRef.current, spread: 3,
    });
    selRef.current = null;
    const committedSwap = swapRef.current;
    swapRef.current = null;
    if (!res) { setPhase("idle"); setBoard((b) => b.map((g) => ({ ...g, fx: "" }))); return null; }
    setLast(res);
    if (res.won) {
      setCombo((c) => c + 1);
      const meterFull = meter + 1 >= METER_MAX;
      setMeter(meterFull ? 0 : meter + 1);
      const extra = (isGold ? 1 : 0) + (meterFull ? 1 : 0);
      if (isGold && meterFull) setBonusMsg("👑 GOLD GEM + 🎰 METER FULL — +2 bonus progress!");
      else if (isGold) setBonusMsg("👑 GOLD GEM — double level progress!");
      else if (meterFull) setBonusMsg("🎰 BONUS METER FULL — +1 extra progress!");
      addProgress(1 + extra);
      setPhase("win");
      cascade(idx);
    } else {
      setCombo(0);
      setMeter(0);
      saveLvl({ ...lvl, wins: 0 }); // 🍬 Candy-Crush rule: a crack fails the level — retry its wins
      setPhase("lose");
      setBoard((b) => b.map((g, i) => (i === idx ? { ...g, fx: "crack" } : { ...g, fx: "" })));
      setTimeout(() => {
        setBoard((b) => {
          let nb = b.map((g, i) => (i === idx ? { ...g } : { ...g, fx: "" }));
          const ng = mkGem("x"); ng.t = safeType(nb, idx); ng.fx = "fall";
          nb[idx] = ng;
          if (committedSwap) { // ↩️ snap the failed swap back
            const { i, j } = committedSwap;
            const t = nb[i]; nb[i] = nb[j]; nb[j] = t;
          }
          return nb;
        });
      }, 950);
    }
    timer.current = setTimeout(() => setPhase("idle"), 1600);
    return res;
  };

  // 🫳 SLIDE-TO-MATCH — drag a gem into a neighbour, they swap, the contract fires
  const doSwap = (i, j) => {
    setSwapFx({ i, j });
    setTimeout(() => {
      setBoard((b) => { const nb = [...b]; const t = nb[i]; nb[i] = nb[j]; nb[j] = t; return nb; });
      setSwapFx(null);
      swapRef.current = { i, j };
      selRef.current = j; // the dragged gem now sits at j
      auto.burst(play);
    }, 240);
  };
  const onDown = (e, i) => {
    if (busy || auto.active) return;
    dragRef.current = { i, x: e.clientX, y: e.clientY };
  };
  const onUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || busy || auto.active) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    let j = null;
    if (Math.max(Math.abs(dx), Math.abs(dy)) >= 14) {
      if (Math.abs(dx) > Math.abs(dy)) j = dx > 0 ? (d.i % COLS < COLS - 1 ? d.i + 1 : null) : (d.i % COLS > 0 ? d.i - 1 : null);
      else j = dy > 0 ? (d.i + COLS < N ? d.i + COLS : null) : (d.i - COLS >= 0 ? d.i - COLS : null);
    }
    if (j != null) doSwap(d.i, j);
    else { selRef.current = d.i; auto.burst(play); } // plain tap still plays
  };
  const swapStyle = (i) => {
    if (!swapFx) return {};
    const { i: a, j: b } = swapFx;
    if (i !== a && i !== b) return {};
    const from = i === a ? a : b, to = i === a ? b : a;
    const dc = (to % COLS) - (from % COLS), dr = Math.floor(to / COLS) - Math.floor(from / COLS);
    return { transform: `translate(calc(${dc * 100}% + ${dc * 4}px), calc(${dr * 100}% + ${dr * 4}px))`, transition: "transform .24s ease", zIndex: 5 };
  };

  const lowBal = balance > 0 && balance < tier.minBal;
  const mapRows = Array.from({ length: Math.max(lvl.level + 4, 9) }, (_, i) => i + 1);
  return (
    <div data-testid="gems-game" className="relative -m-5 sm:-m-6 rounded-xl overflow-hidden">
      {/* 🎰 casino backdrop */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/arcade/gemrush_bg.jpeg)" }} />
      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(8,6,14,0.82), rgba(8,6,14,0.70) 40%, rgba(8,6,14,0.88)), ${theme.tint}` }} />
      <div className="relative space-y-3 p-4 sm:p-5 max-w-xl mx-auto">
        {levelUp && <Confetti />}
        <style>{`
          @keyframes gemPop { 0%{transform:scale(1)} 45%{transform:scale(1.4) rotate(10deg); filter:brightness(2)} 100%{transform:scale(0); opacity:0} }
          @keyframes gemFall { 0%{transform:translateY(-52px); opacity:0} 60%{opacity:1} 80%{transform:translateY(5px)} 100%{transform:translateY(0)} }
          @keyframes gemPulse { 0%,100%{transform:scale(1); filter:brightness(1)} 50%{transform:scale(1.14); filter:brightness(1.7)} }
          @keyframes gemCrack { 0%{filter:none} 30%{transform:rotate(-7deg)} 60%{transform:rotate(6deg)} 100%{filter:grayscale(1) brightness(0.4)} }
          @keyframes gemMerge { 0%{filter:brightness(1)} 50%{transform:scale(1.12); filter:brightness(2.1)} 100%{transform:scale(1.04); filter:brightness(1.5)} }
          @keyframes goldSpin { 0%,100%{filter:brightness(1) drop-shadow(0 0 4px rgba(255,215,0,.7))} 50%{filter:brightness(1.5) drop-shadow(0 0 12px rgba(255,215,0,.95))} }
          @keyframes neonGlow { 0%,100%{text-shadow:0 0 8px rgba(255,215,0,.8),0 0 22px rgba(255,120,220,.45)} 50%{text-shadow:0 0 16px rgba(255,215,0,1),0 0 36px rgba(255,120,220,.7)} }
          .gem-pop{animation:gemPop .42s ease-in forwards}
          .gem-fall{animation:gemFall .5s cubic-bezier(.2,.9,.3,1.2)}
          .gem-pulse{animation:gemPulse .7s ease-in-out infinite}
          .gem-crack{animation:gemCrack .9s ease forwards}
          .gem-merge{animation:gemMerge .24s ease forwards}
          .gem-gold{animation:goldSpin 1.6s ease-in-out infinite}
          .gem-neon{animation:neonGlow 2.2s ease-in-out infinite}
          .gem-shine::after{content:"";position:absolute;top:12%;left:16%;width:34%;height:22%;background:rgba(255,255,255,.55);border-radius:50%;filter:blur(1px)}
        `}</style>

        {/* 🎰 neon marquee header */}
        <div className="flex items-center gap-2 flex-wrap">
          <Gem className="w-5 h-5" style={{ color: "#ffd700" }} />
          <h3 className="gem-neon text-lg font-black tracking-widest text-[#ffd700]">GEM RUSH</h3>
          <span className="rounded-full border border-[#ffd700]/50 bg-[#ffd700]/10 px-2 py-0.5 text-[8px] font-mono font-black text-[#ffd700] tracking-widest">CASINO</span>
          <span className="text-[9px] font-mono text-white/45">real Digit Over · 1 tick</span>
          <HowToPlay
            title="Gem Rush"
            steps={[
              "SLIDE any gem into a neighbour (up/down/left/right) — they swap and ONE real Deriv Digit Over contract fires. A plain tap works too. Like Candy Crush, the board never starts with a ready-made match — YOU create it.",
              "WIN → your gem forms a 3-LINE MATCH that EXPLODES, new gems slide in, your level progress climbs. Level 1 needs 3 wins, each level needs one more (max 10). Tap 🗺 MAP for the journey.",
              "LOSE → the swap snaps back, the gem cracks and the CURRENT level's progress resets — like failing a Candy Crush board. The money loss is real either way.",
              "👑 GOLD GEMS: win on a gold gem = DOUBLE level progress. 🎰 BONUS METER: every 4 straight wins = +1 extra progress. Bonuses speed up levels only — they NEVER change odds, payouts or stakes.",
              "Higher levels unlock riskier tiers: JUICY at LV3, WILD at LV6 — bigger payouts, lower win chance.",
            ]}
            win={`the next tick's last digit is OVER ${tier.bar} (${tier.desc}).`}
            payout={`live Deriv odds — roughly ${tier.pays} per win on ${tier.name}. Higher win chance = smaller payout.`}
            tip="slides, gold gems, meters and themes are pure presentation — only the digit decides. Bank your wins; the house edge never sleeps." />
          <BurstCooldownChip id="gems" />
        </div>

        {/* tier chips — casino style, harder tiers unlock by level */}
        <div className="grid grid-cols-3 gap-1.5">
          {TIERS.map((t) => {
            const locked = lvl.level < t.unlock;
            const active = !locked && tier.key === t.key;
            return (
              <button key={t.key} data-testid={`gems-tier-${t.key}`} disabled={busy || auto.active || locked}
                onClick={() => setTier(t)}
                className={`relative rounded-xl border-2 px-2 py-2 text-left transition backdrop-blur-sm ${locked ? "opacity-45 border-white/10 bg-black/40" : active ? "bg-black/55" : "border-white/15 bg-black/40 opacity-75 hover:opacity-100"}`}
                style={active ? { borderColor: t.color, boxShadow: `0 0 14px ${t.color}55, inset 0 0 10px ${t.color}22` } : {}}>
                <span className="block text-[11px] font-black tracking-wide" style={{ color: t.color }}>{t.name} · Over {t.bar}</span>
                <span className="block text-[9px] font-mono text-white/60">{t.odds} win · pays {t.pays}</span>
                {locked
                  ? <span data-testid={`gems-tier-locked-${t.key}`} className="flex items-center gap-1 text-[8.5px] font-mono font-black text-white/60"><Lock className="w-2.5 h-2.5" /> unlocks at LEVEL {t.unlock}</span>
                  : <span className="block text-[8.5px] font-mono text-white/40">{t.desc} · best ≥ ${t.minBal}</span>}
              </button>
            );
          })}
        </div>
        {lowBal && (
          <p data-testid="gems-balance-hint" className="text-[9px] font-mono text-[#F7B731]">
            💡 {tier.name} suits accounts ≥ ${tier.minBal} — small payouts mean one loss eats several wins. A smaller stake or SWEET mode is kinder to this balance.
          </p>
        )}

        {/* level + bonus meter bar */}
        <div className="flex items-center gap-2 flex-wrap rounded-xl border border-[#ffd700]/30 bg-black/55 backdrop-blur-sm px-3 py-2">
          <span data-testid="gems-level" className="text-[10px] font-mono font-black text-[#F7B731]">⭐ LEVEL {lvl.level}</span>
          <span className="text-[8.5px] font-mono font-black" style={{ color: theme.accent }}>{theme.name}</span>
          <div className="flex-1 min-w-[70px] h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#b86bff] via-[#ffd700] to-[#F7B731] transition-all" style={{ width: `${(lvl.wins / need) * 100}%` }} />
          </div>
          <span data-testid="gems-level-progress" className="text-[9px] font-mono text-white/50">{lvl.wins}/{need} wins</span>
          {combo >= 2 && <span data-testid="gems-combo" className="text-[10px] font-mono font-black text-[#089981]">🔥 ×{combo}</span>}
          <span data-testid="gems-bonus-meter" className="flex items-center gap-0.5 text-[9px] font-mono font-black text-[#ffd700]" title="4 straight wins = +1 bonus progress">
            🎰 {Array.from({ length: METER_MAX }, (_, i) => (<span key={i} className={`inline-block w-1.5 h-3 rounded-sm ${i < meter ? "bg-[#ffd700]" : "bg-white/15"}`} />))}
          </span>
          <button data-testid="gems-map-btn" onClick={() => setMapOpen(true)}
            className="flex items-center gap-1 rounded-md border border-[#ffd700]/40 px-2 py-1 text-[9px] font-mono font-black text-[#ffd700] hover:bg-[#ffd700]/10 transition-colors">
            <Map className="w-3 h-3" /> MAP
          </button>
        </div>

        {/* 💎 the board — gold casino frame, slide-to-match */}
        <div data-testid="gems-board" className="relative rounded-2xl p-2.5"
          style={{ border: "2px solid rgba(255,215,0,0.45)", boxShadow: "0 0 22px rgba(255,215,0,0.18), inset 0 0 30px rgba(0,0,0,0.6)", background: "rgba(10,8,18,0.55)", backdropFilter: "blur(2px)", touchAction: "none" }}>
          {levelUp && (
            <div data-testid="gems-levelup-overlay" className="absolute inset-0 z-20 grid place-items-center rounded-2xl bg-black/60 backdrop-blur-[2px]">
              <div className="text-center space-y-1">
                <span className="gem-neon block text-2xl font-black text-[#ffd700] animate-bounce">⭐ LEVEL {levelUp.cleared} CLEARED! ⭐</span>
                <span className="block text-[11px] font-mono font-black text-white/85">Level {levelUp.next} — {winsNeeded(levelUp.next)} wins to clear</span>
                {levelUp.unlockName && <span className="block text-[11px] font-mono font-black text-[#089981]">🔓 {levelUp.unlockName} tier unlocked!</span>}
                {themeFor(levelUp.next).name !== themeFor(levelUp.cleared).name && <span className="block text-[10px] font-mono text-white/70">🎨 new world: {themeFor(levelUp.next).name}</span>}
              </div>
            </div>
          )}
          <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0,1fr))` }}>
            {board.map((g, i) => (
              <button key={g.id} data-testid={`gems-gem-${i}`} disabled={busy || auto.active}
                onPointerDown={(e) => onDown(e, i)} onPointerUp={onUp}
                className={`relative aspect-square gem-shine ${g.gold ? "gem-gold" : ""} ${g.fx ? `gem-${g.fx}` : ""} ${sel === i && phase === "match" ? "ring-2 ring-white/70" : ""}`}
                style={{ background: g.gold ? GOLD.g : GEMS[g.t].g, borderRadius: g.gold ? GOLD.r : GEMS[g.t].r, boxShadow: `0 2px 6px rgba(0,0,0,.5), inset 0 -3px 6px rgba(0,0,0,.35)`, ...swapStyle(i) }}>
                {g.gold && <span className="absolute inset-0 grid place-items-center text-xs sm:text-sm pointer-events-none">👑</span>}
              </button>
            ))}
          </div>
          {phase === "match" && <p className="mt-2 text-center text-[9px] font-mono text-white/55 animate-pulse">💎 matching… contract settling on {symRef.current}</p>}
          {phase === "win" && last && (
            <p data-testid="gems-result-win" className="gem-neon mt-2 text-center text-sm font-mono font-black text-[#ffd700]">✨ BIG MATCH! digit {last.digit ?? "—"} · +${(last.profit ?? 0).toFixed(2)} ✨</p>
          )}
          {phase === "lose" && last && (
            <p data-testid="gems-result-lose" className="mt-2 text-center text-[11px] font-mono font-black text-[#F23645]">🪨 BUST — digit {last.digit ?? "—"} wasn't over {tier.bar} · −${Math.abs(last.profit ?? 0).toFixed(2)} · level progress reset</p>
          )}
          {bonusMsg && phase === "win" && <p data-testid="gems-bonus-msg" className="mt-1 text-center text-[10px] font-mono font-black text-[#ffd700]">{bonusMsg}</p>}
          <p className="mt-1.5 text-center text-[8.5px] font-mono text-white/35">🫳 slide a gem into a neighbour to match — or just tap one</p>
        </div>

        {/* scanner + auto-run — same engine as every arcade game */}
        <MarketScanner id="gems" mode="hilo" bar={tier.bar} onChange={onScan} />
        <AutoRunBar id="gems" auto={auto} noun="taps" />

        <p className="text-center text-[9px] font-mono text-white/40">every slide/tap is one real Deriv Digit Over {tier.bar} contract ({tier.odds} true odds) · gold gems, meters, levels and themes are presentation only — they never change the odds · losses are real, bank your wins</p>
      </div>

      {/* 🗺 LEVEL MAP — Candy-Crush-style journey */}
      {mapOpen && (
        <div data-testid="gems-map-modal" className="fixed inset-0 z-[80] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setMapOpen(false)}>
          <div className="w-full max-w-sm max-h-[80vh] overflow-y-auto rounded-xl border border-[#ffd700]/40 bg-[#131722] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-black text-[#ffd700]">🗺 Gem Rush — Level Map</h4>
              <button data-testid="gems-map-close" onClick={() => setMapOpen(false)} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4 text-white/60" /></button>
            </div>
            <div className="space-y-1.5">
              {mapRows.map((n) => {
                const th = themeFor(n);
                const cleared = n < lvl.level;
                const current = n === lvl.level;
                const unlockTier = TIERS.find((t) => t.unlock === n && n > 1);
                return (
                  <div key={n} data-testid={`gems-map-level-${n}`}
                    className={`rounded-lg border px-3 py-2 ${current ? "border-[#F7B731]/60 bg-[#F7B731]/5" : cleared ? "border-[#089981]/40 bg-[#089981]/5" : "border-white/10 opacity-60"}`}>
                    <div className="flex items-center gap-2 text-[10px] font-mono">
                      <span className="font-black" style={{ color: th.accent }}>LV {n}</span>
                      <span className="text-white/50">{winsNeeded(n)} wins to clear</span>
                      {unlockTier && <span className="font-black" style={{ color: unlockTier.color }}>🔓 {unlockTier.name}</span>}
                      {(n - 1) % 3 === 0 && <span className="text-white/40">🎨 {th.name}</span>}
                      <span className="ml-auto font-black">
                        {cleared ? <span className="text-[#089981]">⭐ CLEARED</span> : current ? <span className="text-[#F7B731]">▶ {lvl.wins}/{winsNeeded(n)}</span> : <span className="text-white/30">🔒</span>}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-[9px] font-mono text-white/40">a loss resets the CURRENT level's win progress (retry the level) — cleared levels stay cleared. Levels are for fun: every slide is still a real contract with real money at risk.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GemRush;
